package com.milta.feature.aicredit.domain.entity

import com.milta.feature.admin.domain.entity.AdminAccount
import com.milta.feature.optimizationquota.domain.entity.AdminQuotaAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationSubscriptionKind

/**
 * MIL-98 — one account's AI credit for the current subscription period, as the user sees it: a
 * percentage only, never euros nor tokens (R1). Each account has its own (R8). [resetAt] is the ISO
 * instant of the next renewal, when the credit goes back to the limit without carry-over (R9).
 */
data class AiCredit(
    /** 0 once the credit is used up or below 0 (R5), at least 1 while anything is left. */
    val remainingPercent: Int,
    /** Under the admin threshold (R12): orange percentage and banner once per period. */
    val isLow: Boolean,
    /** AI actions disabled until [resetAt] (R4). */
    val isExhausted: Boolean,
    val lowThresholdPercent: Int,
    val resetAt: String,
    val accountKind: OptimizationAccountKind,
    val subscriptionKind: OptimizationSubscriptionKind,
    val isManuallyAdjusted: Boolean,
)

/** The AI call behind a history row — decoded leniently (see [fromApi]): a new server value never breaks the list. */
enum class AiCreditAction {
    ScanDeliveryList,
    ScanPackageLabel,
    ImportPdf,
    /** Universal import (StartImport / RetryImport) — "Import en tâche de fond". */
    ImportUnstructured,
    SpreadsheetMapping,
    ;

    companion object {
        fun fromApi(value: String): AiCreditAction? = entries.firstOrNull { it.name == value }
    }
}

/** Failed and unreadable calls are counted too: Anthropic bills them (R3). */
enum class AiCreditOutcome {
    Success,
    Failed,
    Unreadable,
    ;

    companion object {
        /** An unknown value is shown as a success row rather than failing the page. */
        fun fromApi(value: String): AiCreditOutcome = entries.firstOrNull { it.name == value } ?: Success
    }
}

/** User history row: [creditPercent] is the share of the applied limit this call used. */
data class AiCreditHistoryEntry(
    val id: String,
    val occurredAt: String,
    /** `null` for an action this app version doesn't know yet (shown as "—"). */
    val action: AiCreditAction?,
    val outcome: AiCreditOutcome,
    val creditPercent: Double,
)

/** Admin "Limites d'usage", tab "Tokens IA" (R10, R12) — amounts in euros. */
data class AiCreditSettings(
    val enterpriseTrialEur: Double,
    val enterprisePaidEur: Double,
    val driverTrialEur: Double,
    val driverPaidEur: Double,
    /** 1 € = [usdPerEur] $ — only for the calls that follow a change. */
    val usdPerEur: Double,
    val lowThresholdPercent: Int,
)

/** Admin view of an account's credit — everything in euros, plus the weighted tokens left. */
data class AdminAiCredit(
    /** Can be below 0 after the last call (R5). */
    val remainingEur: Double,
    /** Applied limit: [globalLimitEur], or consumed + remaining after a manual replacement (R11). */
    val limitEur: Double,
    val globalLimitEur: Double,
    val consumedEur: Double,
    val remainingPercent: Int,
    val remainingWeightedTokens: Long,
    val isLow: Boolean,
    val isExhausted: Boolean,
    val resetAt: String,
    val subscriptionKind: OptimizationSubscriptionKind,
    val isManuallyAdjusted: Boolean,
    /** Summary line of the current period (A2). */
    val periodStart: String?,
    val periodCallCount: Int,
    val periodFailedOrUnreadableCount: Int,
    val periodWeightedTokens: Long,
)

/** Admin "Fiche utilisateur" — [enterpriseName] is null for an independent driver. */
data class AdminAccountAiCredit(
    val account: AdminAccount,
    val accountKind: AdminQuotaAccountKind,
    val enterpriseName: String?,
    val credit: AdminAiCredit,
)

/** Admin history row: the call's tokens and its cost in euros. */
data class AdminAiCreditHistoryEntry(
    val id: String,
    val occurredAt: String,
    val action: AiCreditAction?,
    val outcome: AiCreditOutcome,
    val inputTokens: Long,
    val outputTokens: Long,
    val weightedTokens: Long,
    val costEur: Double,
)
